import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Search, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown,
  Filter, Users, RefreshCw, Download
} from 'lucide-react';
import { DnaTableV4 } from '@/design-system/DnaDesignSystem';

// Status styles
const STATUS_STYLES = {
  excelente: { bg: 'dna-tone-success', text: 'dna-tone-success', badge: 'dna-solid-success' },
  satisfatorio: { bg: 'dna-tone-info', text: 'dna-tone-info', badge: 'dna-solid-info' },
  critico: { bg: 'dna-tone-danger', text: 'dna-tone-danger', badge: 'dna-solid-danger' }
};

// Nota Cell Component
function NotaCell({ valor, tipo }) {
  if (valor === null || valor === undefined) {
    return <span className="text-slate-300">-</span>;
  }
  
  const getColor = (v) => {
    if (v >= 8) return 'text-green-600 font-semibold';
    if (v >= 6) return 'text-blue-600';
    return 'text-red-600 font-semibold';
  };
  
  return (
    <span className={getColor(valor)}>
      {(Math.round(valor * 2) / 2).toFixed(1)}
    </span>
  );
}

export default function TabelaNotas({ year, trimestre, turmaId, disciplinaId: propDisciplinaId, filtros }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ registros: [], total: 0 });
  
  // Filtros locais
  const [search, setSearch] = useState('');
  const [disciplinaId, setDisciplinaId] = useState(propDisciplinaId || '');
  const [tipoAvaliacao, setTipoAvaliacao] = useState('');
  const [status, setStatus] = useState('');
  
  // Ordenação e paginação
  const [orderBy, setOrderBy] = useState('nome');
  const [orderDir, setOrderDir] = useState('asc');
  const [page, setPage] = useState(1);
  const [limit] = useState(30);
  
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        year,
        limit,
        offset: (page - 1) * limit,
        order_by: orderBy,
        order_dir: orderDir
      };
      
      if (trimestre) params.trimestre = trimestre;
      if (turmaId) params.turma_id = turmaId;
      if (disciplinaId) params.disciplina_id = disciplinaId;
      if (tipoAvaliacao) params.tipo_avaliacao = tipoAvaliacao;
      if (status) params.status = status;
      if (search) params.search = search;
      
      const res = await api.get('/central-notas/tabela-notas', { params });
      setData(res.data);
    } catch (error) {
      console.error('Erro ao carregar tabela:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [year, trimestre, turmaId, disciplinaId, tipoAvaliacao, status, search, orderBy, orderDir, page, limit]);
  
  useEffect(() => {
    const debounce = setTimeout(() => {
      loadData();
    }, 300);
    return () => clearTimeout(debounce);
  }, [loadData]);
  
  const handleSort = (column) => {
    if (orderBy === column) {
      setOrderDir(orderDir === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(column);
      setOrderDir('asc');
    }
  };
  
  const SortIcon = ({ column }) => {
    if (orderBy !== column) return <ArrowUpDown className="w-3 h-3 text-slate-400" />;
    return orderDir === 'asc' 
      ? <ArrowUp className="w-3 h-3 text-purple-600" />
      : <ArrowDown className="w-3 h-3 text-purple-600" />;
  };
  
  const totalPages = Math.ceil(data.total / limit);
  
  return (
    <div className="space-y-4" data-testid="tabela-notas">
      {/* Filtros */}
      <Card className="bg-white border border-purple-200/50 shadow-sm">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Busca */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar aluno..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-10 bg-slate-50 border-slate-200"
                data-testid="search-input"
              />
            </div>
            
            {/* Disciplina */}
            <Select value={disciplinaId || "all"} onValueChange={(v) => { setDisciplinaId(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="w-[180px] bg-slate-50 border-slate-200">
                <SelectValue placeholder="Disciplina" />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-[300px]">
                <SelectItem value="all">Todas</SelectItem>
                {(filtros?.disciplinas || []).map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Tipo de Avaliação */}
            <Select value={tipoAvaliacao || "all"} onValueChange={(v) => { setTipoAvaliacao(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="w-[160px] bg-slate-50 border-slate-200">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Todos</SelectItem>
                {(filtros?.tipos_avaliacao || []).filter(t => !t.toLowerCase().includes('bimestral')).map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Status */}
            <Select value={status || "all"} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="w-[140px] bg-slate-50 border-slate-200">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="satisfatorio">Satisfatório</SelectItem>
                <SelectItem value="atencao">Atenção</SelectItem>
                <SelectItem value="critico">Crítico</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Info */}
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Users className="w-4 h-4" />
              <span>{data.total} registros</span>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Tabela */}
      <DnaTableV4 stickyHeader maxHeight="70vh" className="dna-notas-table-v4" testId="notas-table-v4">
        <div className="dna-responsive-table sticky-first-col">
          <table className="w-full text-sm" data-testid="notas-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="sticky left-0 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-700">
                  <button 
                    onClick={() => handleSort('nome')}
                    className="flex items-center gap-1 hover:text-purple-600"
                  >
                    Aluno <SortIcon column="nome" />
                  </button>
                </th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">
                  <button 
                    onClick={() => handleSort('turma')}
                    className="flex items-center gap-1 hover:text-purple-600"
                  >
                    Turma <SortIcon column="turma" />
                  </button>
                </th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">
                  <button 
                    onClick={() => handleSort('disciplina')}
                    className="flex items-center gap-1 hover:text-purple-600"
                  >
                    Disciplina <SortIcon column="disciplina" />
                  </button>
                </th>
                <th className="px-3 py-3 text-center font-semibold text-slate-700 bg-blue-50">APA</th>
                <th className="px-3 py-3 text-center font-semibold text-slate-700 bg-blue-50">Trabalho</th>
                <th className="px-3 py-3 text-center font-semibold text-slate-700 bg-blue-50">Prontidão</th>
                <th className="px-3 py-3 text-center font-semibold text-slate-700 bg-purple-50">Av. Trimestral</th>
                <th className="px-3 py-3 text-center font-semibold text-slate-700 bg-orange-50">Recup.</th>
                <th className="px-3 py-3 text-center font-semibold text-slate-700 bg-orange-50">Recup. Paralela</th>
                <th className="px-3 py-3 text-center font-semibold text-slate-700">
                  <button 
                    onClick={() => handleSort('media')}
                    className="flex items-center gap-1 hover:text-purple-600 mx-auto"
                  >
                    Média <SortIcon column="media" />
                  </button>
                </th>
                <th className="px-3 py-3 text-center font-semibold text-slate-700">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="px-4 py-3"><Skeleton className="h-5 w-40 bg-slate-100" /></td>
                    <td className="px-3 py-3"><Skeleton className="h-5 w-32 bg-slate-100" /></td>
                    <td className="px-3 py-3"><Skeleton className="h-5 w-24 bg-slate-100" /></td>
                    {Array.from({ length: 7 }).map((_, j) => (
                      <td key={j} className="px-3 py-3"><Skeleton className="h-5 w-10 mx-auto bg-slate-100" /></td>
                    ))}
                    <td className="px-3 py-3"><Skeleton className="h-5 w-16 mx-auto bg-slate-100" /></td>
                  </tr>
                ))
              ) : data.registros.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-12 text-center text-slate-500">
                    Nenhum registro encontrado
                  </td>
                </tr>
              ) : (
                data.registros.map((reg, idx) => {
                  const statusStyle = STATUS_STYLES[reg.status] || STATUS_STYLES.atencao;
                  return (
                    <tr 
                      key={`${reg.student_id}-${reg.disciplina_id}-${idx}`}
                      className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${reg.status === 'critico' ? 'bg-red-50/30' : ''}`}
                    >
                      <td className="sticky left-0 bg-white px-4 py-3">
                        <div className="flex items-center gap-3">
                          {reg.foto ? (
                            <img src={reg.foto} alt={reg.nome} className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold">
                              {reg.nome?.charAt(0) || '?'}
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-slate-800 truncate max-w-[200px]">{reg.nome}</p>
                            {reg.matricula && (
                              <p className="text-xs text-slate-400">{reg.matricula}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-slate-600 text-xs">{reg.turma}</td>
                      <td className="px-3 py-3 text-slate-600">{reg.disciplina}</td>
                      <td className="px-3 py-3 text-center bg-blue-50/30"><NotaCell valor={reg.APA} /></td>
                      <td className="px-3 py-3 text-center bg-blue-50/30"><NotaCell valor={reg.Trabalho} /></td>
                      <td className="px-3 py-3 text-center bg-blue-50/30"><NotaCell valor={reg.Prontidão} /></td>
                      <td className="px-3 py-3 text-center bg-purple-50/30"><NotaCell valor={reg['Avaliação Trimestral']} /></td>
                      <td className="px-3 py-3 text-center bg-orange-50/30"><NotaCell valor={reg.Recuperação} /></td>
                      <td className="px-3 py-3 text-center bg-orange-50/30"><NotaCell valor={reg['Recuperação Paralela']} /></td>
                      <td className="px-3 py-3 text-center">
                        <span className={`text-lg font-bold ${
                          reg.media_final >= 6 ? 'text-green-600' :
                          reg.media_final >= 4 ? 'text-orange-600' : 'text-red-600'
                        }`}>
                          {reg.media_final != null ? (Math.round(reg.media_final * 2) / 2).toFixed(1) : '-'}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <Badge className={`${statusStyle.badge} text-white text-xs`}>
                          {reg.status === 'satisfatorio' ? 'OK' : 
                           reg.status === 'atencao' ? 'Atenção' : 'Crítico'}
                        </Badge>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </DnaTableV4>
      
      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="border-slate-200"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) pageNum = i + 1;
              else if (page <= 3) pageNum = i + 1;
              else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
              else pageNum = page - 2 + i;
              
              return (
                <Button
                  key={pageNum}
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(pageNum)}
                  className={`w-8 border-slate-200 ${
                    page === pageNum 
                      ? 'bg-purple-500 text-white border-purple-500' 
                      : 'hover:bg-purple-50'
                  }`}
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="border-slate-200"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          
          <span className="text-slate-500 text-sm ml-2">
            Página {page} de {totalPages}
          </span>
        </div>
      )}
    </div>
  );
}
